/**
 * Negotiation terms helpers — SCF 360-day APR convention.
 *
 * In supply-chain finance the buyer's perspective is that early-pay APR is
 * *yield* — higher is better.  The "floor" is a *minimum* effective APR the
 * buyer will accept (the yield must stay at or above the cost of capital).
 *
 * Formula: APR = discountPct × 360 / daysEarly
 * Inverse:  discountPct = APR × daysEarly / 360
 */

/**
 * Convert a discount percentage and days-early value to an annualised APR.
 *
 * Guards:
 *  - daysEarly ≤ 0 → returns 0 (avoids division by zero / negative APR).
 *  - discountPct < 0 → treated as 0 (no meaningful negative discount).
 *
 * @param discountPct  The discount as a percentage (e.g. 2 for 2 %).
 * @param daysEarly    Number of days paid ahead of the net due date (must be > 0).
 * @returns Effective annual percentage rate (e.g. 36 for 36 %).
 */
export function discountToApr(discountPct: number, daysEarly: number): number {
  if (!Number.isFinite(daysEarly) || daysEarly <= 0) return 0;
  if (!Number.isFinite(discountPct) || discountPct < 0) return 0;
  return (discountPct * 360) / daysEarly;
}

/**
 * Convert an APR and days-early value back to a discount percentage.
 *
 * Guards:
 *  - daysEarly ≤ 0 → returns 0.
 *  - apr < 0 → treated as 0.
 *
 * @param apr         Effective annual percentage rate (e.g. 36).
 * @param daysEarly   Number of days paid early (must be > 0).
 * @returns Discount percentage (e.g. 2 for 2 %).
 */
export function aprToDiscount(apr: number, daysEarly: number): number {
  if (!Number.isFinite(daysEarly) || daysEarly <= 0) return 0;
  if (!Number.isFinite(apr) || apr < 0) return 0;
  return (apr * daysEarly) / 360;
}

/**
 * Format an APR number as a percentage string with one decimal place.
 * e.g. 12 → "12.0%", 3.5 → "3.5%"
 */
export function formatApr(apr: number): string {
  return `${apr.toFixed(1)}%`;
}

/**
 * Format a discount percentage with two decimal places.
 * e.g. 2 → "2.00%", 1.5 → "1.50%"
 */
export function formatDiscount(discountPct: number): string {
  return `${discountPct.toFixed(2)}%`;
}
